// Reportes del área "Construcción" para el Centro de Reportes. Recibe las
// funciones de descarga de Reports.js para mantener un solo formato de CSV/PDF.
import { FileSpreadsheet, FileText } from 'lucide-react';
import { formatIndex } from './money';
import { t } from '../i18n';

const round = (value) => Math.round(Number(value) || 0);
const pct = (value) => (value == null || !Number.isFinite(value) ? '' : Number(value).toFixed(1));

export const buildConstructionReports = ({
  summaries,
  state,
  selectedParkId,
  projectLabel,
  suffix,
  displayCurrency,
  convert,
  userName,
  downloadCsv,
  downloadPdf
}) => {
  const list = [...summaries.values()].filter((summary) => !selectedParkId || summary.parkId === selectedParkId);
  const parkIds = new Set(list.map((summary) => summary.parkId));
  const parkName = Object.fromEntries(list.map((summary) => [summary.parkId, summary.parkName]));
  const money = (summary, amount) => round(convert(amount, summary.currency, displayCurrency));
  const itemName = Object.fromEntries(state.wbsItems.map((item) => [item.id, `${item.code} ${item.name}`]));

  return [
    {
      id: 'construction-schedule',
      title: t('Cronograma de obra (CSV)'),
      description: t('EDT/WBS con línea base, fechas reales, predecesoras y avance para {0}.', projectLabel),
      icon: FileSpreadsheet,
      action: () => {
        const headers = ['Proyecto', 'Código', 'Partida', 'Ponderación %', 'Inicio LB', 'Fin LB', 'Inicio real', 'Fin real', 'Predecesoras', 'Responsable', 'Avance %'];
        const rows = list.flatMap((summary) =>
          summary.evm.rows.map((row) => [
            summary.parkName,
            row.code,
            row.name,
            pct(row.weightPct),
            row.baselineStart || '',
            row.baselineEnd || '',
            row.actualStart || '',
            row.actualEnd || '',
            (row.predecessors || []).map((id) => itemName[id]).filter(Boolean).join(' | '),
            row.responsible || '',
            pct(row.actual)
          ])
        );
        downloadCsv(`kota_cronograma_obra${suffix}.csv`, headers, rows);
      }
    },
    {
      id: 'construction-progress',
      title: t('Avance físico y curva S (CSV)'),
      description: t('Avance programado vs real por partida y curva S semanal del proyecto.'),
      icon: FileSpreadsheet,
      action: () => {
        const headers = ['Proyecto', 'Tipo', 'Código / Fecha', 'Partida', 'Programado %', 'Real %', 'SPI'];
        const rows = list.flatMap((summary) => [
          ...summary.evm.rows.map((row) => [summary.parkName, 'Partida', row.code, row.name, pct(row.planned), pct(row.actual), formatIndex(row.progressSpi)]),
          ...summary.curve.map((point) => [summary.parkName, 'Curva S', point.date, '', pct(point.planned), point.actual == null ? '' : pct(point.actual), ''])
        ]);
        downloadCsv(`kota_avance_obra${suffix}.csv`, headers, rows);
      }
    },
    {
      id: 'construction-evm',
      title: t('Earned Value (CSV)'),
      description: t('BAC, PV, EV, AC, CV, SV, CPI, SPI, EAC y ETC por partida ({0}).', displayCurrency),
      icon: FileSpreadsheet,
      action: () => {
        const headers = ['Proyecto', 'Código', 'Partida', `BAC ${displayCurrency}`, `PV ${displayCurrency}`, `EV ${displayCurrency}`, `AC ${displayCurrency}`, `CV ${displayCurrency}`, `SV ${displayCurrency}`, 'CPI', 'SPI', `EAC ${displayCurrency}`, `ETC ${displayCurrency}`];
        const rows = list.flatMap((summary) => {
          const p = summary.evm.project;
          return [
            ...summary.evm.rows.map((row) => [summary.parkName, row.code, row.name, money(summary, row.bac), money(summary, row.pv), money(summary, row.ev), money(summary, row.ac), money(summary, row.cv), money(summary, row.sv), formatIndex(row.cpi), formatIndex(row.spi), money(summary, row.eac), money(summary, row.etc)]),
            [summary.parkName, 'TOTAL', 'Proyecto (incluye AC no asignado)', money(summary, p.bac), money(summary, p.pv), money(summary, p.ev), money(summary, p.ac), money(summary, p.cv), money(summary, p.sv), formatIndex(p.cpi), formatIndex(p.spi), money(summary, p.eac), money(summary, p.etc)]
          ];
        });
        downloadCsv(`kota_evm_obra${suffix}.csv`, headers, rows);
      }
    },
    {
      id: 'construction-daily',
      title: t('Partes diarios (CSV)'),
      description: t('Libro de obra: clima, dotación, HH, actividades e incidencias.'),
      icon: FileSpreadsheet,
      action: () => {
        const headers = ['Proyecto', 'Fecha', 'Clima', 'Dotación', 'HH', 'Cuadrillas', 'Actividades', 'Avance del día', 'Equipos', 'Incidencias', 'Técnico'];
        const rows = state.dailyLogs
          .filter((log) => parkIds.has(log.parkId))
          .sort((a, b) => a.date.localeCompare(b.date))
          .map((log) => [
            parkName[log.parkId],
            log.date,
            log.weather,
            (log.crews || []).reduce((sum, crew) => sum + (Number(crew.workers) || 0), 0),
            (log.crews || []).reduce((sum, crew) => sum + (Number(crew.workers) || 0) * (Number(crew.hours) || 0), 0),
            (log.crews || []).map((crew) => `${crew.name} (${crew.workers}x${crew.hours}h)`).join(' | '),
            log.activities,
            log.progressNotes || '',
            log.equipment || '',
            log.incidents || '',
            userName(log.userId)
          ]);
        downloadCsv(`kota_partes_diarios${suffix}.csv`, headers, rows);
      }
    },
    {
      id: 'construction-payments',
      title: t('Estados de pago (PDF)'),
      description: t('Valorizaciones por contratista con estado de aprobación ({0}).', displayCurrency),
      icon: FileText,
      action: () => {
        const contractName = Object.fromEntries(state.contractors.map((contract) => [contract.id, contract.name]));
        const lines = state.paymentStatements
          .filter((statement) => parkIds.has(statement.parkId))
          .map((statement) => {
            const summary = list.find((entry) => entry.parkId === statement.parkId);
            const net = money(summary, statement.netAmount).toLocaleString('es-CL');
            return `${parkName[statement.parkId]} · ${contractName[statement.contractorId] || '-'} · EP ${statement.number} · ${statement.periodEnd} · ${pct(statement.progressPct)}% · Neto ${net} ${displayCurrency} · ${statement.status}`;
          });
        downloadPdf(`kota_estados_pago${suffix}.pdf`, 'Estados de pago', lines.length ? lines : ['Sin estados de pago.']);
      }
    },
    {
      id: 'construction-nc',
      title: t('No conformidades (CSV)'),
      description: t('NC de calidad y HSE con responsable, plazo, estado y cierre.'),
      icon: FileSpreadsheet,
      action: () => {
        const headers = ['Proyecto', 'NC', 'Severidad', 'Partida', 'Responsable', 'Apertura', 'Plazo', 'Estado', 'Cierre', 'Nota de cierre'];
        const rows = state.nonConformities
          .filter((nc) => parkIds.has(nc.parkId))
          .map((nc) => [parkName[nc.parkId], nc.title, nc.severity, itemName[nc.wbsItemId] || '', nc.responsibleName || '', nc.openedAt, nc.dueDate || '', nc.status, nc.closedAt || '', nc.closureNote || '']);
        downloadCsv(`kota_no_conformidades${suffix}.csv`, headers, rows);
      }
    }
  ];
};
